import {
  AgendaCard,
  CardBlock,
  CardBlocks,
  CardCollectionAgendaTemplate,
  EmptyState,
  ListSection,
  QuickCreate,
  Selector,
  SortBy,
  TaskListOptionsV1,
  TaskListOptionsV2,
  evaluateSelector,
  isISODateString,
} from '@coast/schemas';
import { inject } from 'between';
import { JSONSchema7 } from 'json-schema';
import { sortBy } from 'lodash';
import { ReactNode } from 'react';
import { SectionBase, SectionListRenderItemInfo } from 'react-native';
import { CardPaginationFilter } from '@shared/api/providers/cardPagination';
import { Fields } from '@shared/components/blocks/types';
import { extractAllFields } from '@shared/components/blocks/utils';
import { defaultEvaluationContext } from '@shared/components/blocks/utils/tokens';

import { API } from '@shared/types/api';
export type BlockWithTableHeader = Extract<CardBlock, { tableHeader?: string }>;

export interface CardColumn {
  block: BlockWithTableHeader;
  width: string;
}

export interface CardSectionListData extends SectionBase<API.CardFragment> {
  collapsed?: boolean;
  title?: ReactNode;
  cardCount: number;
  isArchived?: boolean;
  isEmpty?: boolean;
  cardBlocks: CardBlocks;
  cardTheme?: ListSection['cardTheme'];
  taskOptions: TaskListOptionsV1 | TaskListOptionsV2;
  quickCreate?: QuickCreate;
  emptyState?: EmptyState;
  loading: boolean;
  hasMore: boolean;
  columnBlocks?: CardColumn[];
  fetchMore?(): void;
  toggleCollapsed?(): void;
}

export type CardSectionListRenderItemInfo = SectionListRenderItemInfo<
  API.CardFragment,
  CardSectionListData
>;

export function selectAgendaCard(
  card: API.CardFragment,
  definition: JSONSchema7,
  template: CardCollectionAgendaTemplate,
): AgendaCard | undefined {
  const fields = extractAllFields(card, definition);
  return template.cards.find(agendaCard =>
    agendaCard.selector
      ? evaluateSelector(agendaCard.selector, fields, defaultEvaluationContext)
      : false,
  );
}

export function selectCard(selector: Selector, definition: JSONSchema7) {
  return (card: API.CardFragment): boolean =>
    selector
      ? evaluateSelector(selector, extractAllFields(card, definition), defaultEvaluationContext)
      : true;
}

export function sortCards(
  cards: API.CardFragment[],
  sort: SortBy | undefined,
  definition: JSONSchema7,
): API.CardFragment[] {
  if (!sort) {
    return sortBy(cards, card => new Date(card.createdAt));
  }

  const sorted = sortBy(cards, card => {
    const fields = extractAllFields(card, definition);
    const field = fields[sort.field];
    if (isISODateString(field)) {
      return new Date(field);
    }

    return field;
  });

  if (sort.direction === 'DESC') {
    sorted.reverse();
  }
  return sorted;
}

// Note(fant): we make the between package represent kanban orders
// as strings with numerical characters. The default is harder to read:
// https://github.com/dominictarr/between/blob/master/index.js#L4
const Between = inject('0123456789');
export function orderBetweenCards(
  cardAbove: API.CardFragment,
  cardBelow: API.CardFragment,
  cardDefinition: JSONSchema7,
): string {
  const getOrder = (card: API.CardFragment, defaultValue: string): string => {
    if (!card) {
      return defaultValue;
    }
    const fields = extractAllFields(card, cardDefinition);
    if (!fields.kanbanSortKey) {
      return defaultValue;
    }
    return fields.kanbanSortKey;
  };

  const [a, b] = [getOrder(cardBelow, Between.lo), getOrder(cardAbove, String(Date.now()))].sort(
    Between.strord,
  );
  if (a === b) {
    return a;
  }
  return Between.between(a, b);
}

const emptyIds: number[] = [];

export function getFilterFieldOverrides(filters: CardPaginationFilter[]): Fields {
  return filters?.reduce((overrides, filter) => {
    if (filter.type === 'tag') {
      filter.mustHaveAll?.forEach(tag => {
        if (!!overrides[tag.fieldName]) {
          overrides[tag.fieldName].push(tag.slug);
        } else {
          overrides[tag.fieldName] = [tag.slug];
        }
      });
      // When we want to exclude tags, we semi-hackily set the tag to
      // be empty. This is used to "clear" tags in kanban when dropping
      // in the "no tag" section
      filter.mustNotHaveAny?.forEach(tag => {
        overrides[tag.fieldName] = [];
      });
    } else if (filter.type === 'card') {
      filter.mustHaveAll?.forEach(option => {
        if (!!overrides[option.fieldName]) {
          overrides[option.fieldName].push({ id: option.cardId });
        } else {
          overrides[option.fieldName] = [{ id: option.cardId }];
        }
      });
      // When we want to exclude cards, we semi-hackily set the related card to
      // be empty. This is used to "clear" related cards in kanban when dropping
      // in the "no cards" section
      filter.mustNotHaveAny?.forEach(option => {
        overrides[option.fieldName] = [];
      });
    } else if (filter.type === 'user') {
      if (!!overrides.assignees) {
        const mustHaveAllIds = filter.mustHaveAllIds ?? emptyIds;
        overrides.assignees.push(...mustHaveAllIds);
      } else {
        overrides.assignees = filter.mustHaveAllIds;
      }
    }
    return overrides;
  }, {} as Fields);
}

export function getSectionCardCountString(cardCount: number, hasMore: boolean): string {
  if (hasMore) {
    return ` (${cardCount}+)`;
  }
  return ` (${cardCount})`;
}
